// ============================================================
// WEAPON EFFECTS - Registry-based post-hit weapon special effects.
// Replaces scattered if(hasWS) blocks in useCombat applyHits.
// ============================================================
import { rollDie } from './dice';
import { rollPlainTargetTest } from './characteristicTests';
import { hasWS, getWSP } from './weaponSpecials';
import { mergeCondition, haywireSeverityName } from './conditions';
import { isImmuneTo } from './conditionEligibility';
import { rollPinningTest } from './pinning';
import { reactionExposure } from './exposures';
import { resistTest } from './resistances';
import { DRAIN_TABLE, DRAIN_DURATION, mergeDrain, drainUnitLabel, DRAIN_UNITS } from './statDrains';
import type { DrainChar } from './statDrains';
import { getBodyConfig, armourKeyFor } from './bodyConfig';
import { effectiveVehicleFacing, VEHICLE_FACING_LABELS } from './vehicleFacing';
import type { HitLocation, Target } from '../types';
import type { HitResult } from './damageCalc';
import type { StackPolicy } from './stackPolicy';

export interface WeaponEffectContext {
  target: Target;
  hits: HitResult[];
  dos: number;
  specialRules: string[];
  isMachine: boolean;
  /** What a condition does to one already worn (v8.367.0) - the session's, unless the effect names its own. */
  stacking: StackPolicy;
  /** The attacker's team tag, for an effect's `affects` (v8.367.4). Absent = teamless. */
  attackerTeamTag?: string | null;
  /** The attacker's side (2026-10-23), what `affects` compares; absent = the tag. */
  attackerSideTag?: string | null;
  /** The attacker's team tag and exposures (v8.368.5), for what THEIR fire does - a `pin` axis. */
  attackerExposures?: readonly string[];
  newConds: string[];
  newArmour: Record<string, number>;
  slot: { psyRating?: number; [key: string]: unknown };
  parryResult?: { success: boolean; roll: number } | null;
  dealtDamage: boolean;
  targetKilled: boolean;
  /**
   * The attack's single haywire field roll (1d10 + X), decided at the
   * shape so every victim of one blast shares one severity (v8.198.8).
   * 0 / absent = no Haywire on the weapon; the handler then rolls its
   * own as a fallback for direct engine callers.
   */
  haywireRoll?: number;
}

export interface WeaponEffectDetail {
  name: string;
  icon: string;
  roll?: number;
  target?: number;
  success?: boolean;
  result: string;
  conditionApplied?: string;
  extraDamage?: number;
}

export interface WeaponEffectResult {
  narrativeNotes: string[];
  extraDamage: number;
  effectDetails: WeaponEffectDetail[];
}

interface WeaponEffectHandler {
  name: string;
  postHit: (ctx: WeaponEffectContext) => WeaponEffectResult;
}

const WEAPON_EFFECTS: WeaponEffectHandler[] = [];

export function registerWeaponEffect(handler: WeaponEffectHandler): void {
  WEAPON_EFFECTS.push(handler);
}

/**
 * Process all registered weapon effects. Each handler checks its own
 * preconditions (e.g. dealtDamage, not killed) and may mutate
 * ctx.newConds / ctx.newArmour in-place.
 */
export function processAllWeaponEffects(ctx: WeaponEffectContext): WeaponEffectResult {
  const combined: WeaponEffectResult = { narrativeNotes: [], extraDamage: 0, effectDetails: [] };
  for (const handler of WEAPON_EFFECTS) {
    if (!hasWS(ctx.specialRules, handler.name)) continue;
    const result = handler.postHit(ctx);
    combined.narrativeNotes.push(...result.narrativeNotes);
    combined.extraDamage += result.extraDamage;
    combined.effectDetails.push(...result.effectDetails);
  }
  return combined;
}

/** Reset the registry (for testing) */
export function clearWeaponEffects(): void {
  WEAPON_EFFECTS.length = 0;
}

// ============================================================
// Handler helpers - every line a handler writes is BOTH a narrative
// note and a structured detail (the structured log renders details; a
// note alone never reaches the screen - see Toxic's re-roll, v8.194.1).
// ============================================================

/** A handler that did nothing. */
const none = (): WeaponEffectResult => ({ narrativeNotes: [], extraDamage: 0, effectDetails: [] });

/** One line, as a note and a detail (whose `result` is the line). */
function report(detail: WeaponEffectDetail, extraDamage = 0): WeaponEffectResult {
  return { narrativeNotes: [detail.result], extraDamage, effectDetails: [detail] };
}

/** Several handler results in order, as one. */
function combine(...results: WeaponEffectResult[]): WeaponEffectResult {
  return {
    narrativeNotes: results.flatMap((r) => r.narrativeNotes),
    extraDamage: results.reduce((n, r) => n + r.extraDamage, 0),
    effectDetails: results.flatMap((r) => r.effectDetails),
  };
}

/** Most effects need the hit to have done damage to a target still standing. */
const inert = (ctx: WeaponEffectContext) => !ctx.dealtDamage || ctx.targetKilled;

/** A test's detail: who rolled what against what, and how it went. */
function tested(
  who: { name: string; icon: string },
  test: { roll: number; target: number },
  success: boolean,
  result: string,
  rest: Partial<WeaponEffectDetail> = {},
): WeaponEffectDetail {
  return { name: who.name, icon: who.icon, roll: test.roll, target: test.target, success, result, ...rest };
}

/**
 * A Machine cannot be stunned: the line that says so, or null. (From Beyond was spared too until the ruling of
 * 2026-10-11: the book lists Fear, Pinning and the mind, not Stunning.)
 */
function stunImmunity(ctx: WeaponEffectContext, who: { name: string; icon: string }): WeaponEffectResult | null {
  if (!ctx.isMachine) return null;
  return report({
    name: who.name,
    icon: who.icon,
    result: who.icon + ' ' + who.name + ': Target is a Machine - immune to Stun!',
  });
}

/** The last hit that got through and did damage - where acid and crushing land. */
const lastDamagingHit = (ctx: WeaponEffectContext) =>
  ctx.hits.filter((h) => !h.ffBlocked && h.effectiveDamage > 0).slice(-1)[0];

/**
 * Take `amount` AP off the armour a hit on `loc` wore (never below 0), in
 * place: the location's own, or a vehicle's presented facing - the key
 * `resolveArmourAP` reads, since a vehicle keeps armour by facing, not by
 * location. `label` names that armour for the line.
 */
function stripArmour(ctx: WeaponEffectContext, loc: HitLocation, amount: number) {
  const config = getBodyConfig(ctx.target.bodyType);
  const facing = effectiveVehicleFacing(ctx.target);
  const key = armourKeyFor(loc.id, config, facing);
  const oldAP = ctx.newArmour[key] || 0;
  ctx.newArmour[key] = Math.max(0, oldAP - amount);
  const label = config.armourMode === 'facing' ? VEHICLE_FACING_LABELS[facing] : loc.name;
  return { oldAP, now: ctx.newArmour[key], label };
}

// ============================================================
// Register all built-in weapon effect handlers
// ============================================================

const FLAME = { name: 'Flame', icon: '🔥' };

registerWeaponEffect({
  name: 'Flame',
  postHit(ctx) {
    if (inert(ctx)) return none();
    // A Machine cannot catch fire (traitCatalog.ts:270). Checked BEFORE the
    // Ag-test so no die is spent on a roll whose outcome cannot matter.
    if (!hasWS(ctx.specialRules, 'Purgatus') && isImmuneTo(ctx.target as unknown as Record<string, unknown>, 'Fi')) {
      return report({ name: 'Flame', icon: '⚙', success: true, result: '⚙ Machine: immune to fire - no ignition.' });
    }
    // v8.199.3: rolled through the proper test engine - one d100, same
    // base (dodge.agility), unnatural-ready. Condition penalties on the
    // resisting roll remain future work: the house-rule gate does not
    // reach this module yet (see dev-docs/backlog).
    const t = rollPlainTargetTest(ctx.target, 'dodge', 0, 0);
    const vs = ' (' + t.roll + ' vs ' + t.target + ')';
    if (t.success) return report(tested(FLAME, t, true, '🔥 Flame: Pass Ag-test' + vs + ' - no fire.'));
    if (!ctx.newConds.includes('Fi')) ctx.newConds.push('Fi');
    const text = '🔥 Flame: FAIL Ag-test' + vs + ' - ON FIRE! (1d10 E/round, ignores AP)';
    return report(tested(FLAME, t, false, text, { conditionApplied: 'Fi' }));
  },
});

const TOXIC = { name: 'Toxic', icon: '☠' };
type ResistOut = ReturnType<typeof resistTest>;

/** " (+10 Resistance)" when a resistance bonus shaped the test. */
function bonusNote(out: ResistOut): string {
  const { bonus, bonusSources } = out.resistance;
  return bonus ? ' (' + (bonus > 0 ? '+' : '') + bonus + ' ' + bonusSources.join('/') + ')' : '';
}

/** The failed first roll a re-roll replaced - a detail too, or the re-roll is invisible. */
function toxicRerollLine(out: ResistOut, toxRating: number): WeaponEffectResult {
  const first = out.firstTest!;
  const who = out.resistance.rerollSources.join('/');
  const text =
    '☠ Toxic(' +
    toxRating +
    '): FAIL T-test (' +
    first.roll +
    ' vs ' +
    first.target +
    ') - ' +
    who +
    ' re-roll: ' +
    out.test!.roll;
  return report(tested(TOXIC, first, false, text));
}

/** The deciding test: resisted, or 1d10 Impact damage that ignores AP and TB. */
function toxicVerdict(out: ResistOut, toxRating: number): WeaponEffectResult {
  const t = out.test!;
  const vs = ' (' + t.roll + ' vs ' + t.target + bonusNote(out) + ')';
  if (out.passed) return report(tested(TOXIC, t, true, '☠ Toxic(' + toxRating + '): Pass T-test' + vs + ' - resists.'));
  const toxDmg = rollDie(10);
  const text = '☠ Toxic(' + toxRating + '): FAIL T-test' + vs + ' - suffers ' + toxDmg + ' Impact dmg (ignores AP/TB)!';
  return report(tested(TOXIC, t, false, text, { extraDamage: toxDmg }), toxDmg);
}

registerWeaponEffect({
  name: 'Toxic',
  postHit(ctx) {
    if (inert(ctx)) return none();
    // Official BC/OW scaling: −10 per point of X (was −5×X until v8.197.1).
    // v8.347.0: through the RESISTANCE engine, vector `toxin` - Machine's
    // immunity and the Oolitic Kidney re-roll used to be branches here;
    // they are rows in engine/resistances now, beside every talent that
    // resists a toxin. `ctx.isMachine` stays authoritative: the pipeline
    // derived it, and a handler's context is the truth it was given.
    const toxRating = getWSP(ctx.specialRules, 'Toxic');
    const subject = ctx.isMachine && !((ctx.target.machineAP ?? 0) > 0) ? { ...ctx.target, machineAP: 1 } : ctx.target;
    const out = resistTest(subject as unknown as Record<string, unknown>, 'toxin', 'toughness', 0, -toxRating * 10);
    if (out.immune) {
      return report({ ...TOXIC, result: '☠ Toxic: ' + out.resistance.immuneSources.join(', ') + ' - Toxic immune!' });
    }
    return combine(out.rerolled ? toxicRerollLine(out, toxRating) : none(), toxicVerdict(out, toxRating));
  },
});

const SHOCK = { name: 'Shock', icon: '⚡' };

registerWeaponEffect({
  name: 'Shock',
  postHit(ctx) {
    if (inert(ctx)) return none();
    const immune = stunImmunity(ctx, SHOCK);
    if (immune) return immune;
    // v8.199.3: through the test engine - same one d100, same TB×10 base.
    const t = rollPlainTargetTest(ctx.target, 'toughness', 0, 0);
    if (t.success) {
      return report(tested(SHOCK, t, true, '⚡ Shock: Pass T-test (' + t.roll + ' vs ' + t.target + ') - resists.'));
    }
    // Official (errata'd) duration: rounds = degrees of failure, like
    // Concussive - the flat 1 round was a house simplification (v8.197.2).
    const dof = Math.max(1, t.dof);
    mergeCondition(ctx.newConds, 'S' + dof, ctx.stacking);
    const text =
      '⚡ Shock: FAIL T-test (' + t.roll + ' vs ' + t.target + ', ' + dof + ' DoF) - STUNNED for ' + dof + ' round(s)!';
    return report(tested(SHOCK, t, false, text, { conditionApplied: 'S' + dof }));
  },
});

const CONCUSSIVE = { name: 'Concussive', icon: '💥' };

/**
 * Official second clause (v8.197.3), independent of the Stun test: damage
 * exceeding the target's Strength Bonus knocks them Prone. Runs BEFORE the
 * Machine/From Beyond gate - those grant Stun immunity, but a shockwave
 * puts a servitor on the floor all the same.
 */
function concussiveProne(ctx: WeaponEffectContext): WeaponEffectResult {
  const concDmg = ctx.hits.reduce((sum, h) => sum + (h.effectiveDamage || 0), 0);
  const tgtSB = ctx.target.strengthBonus ?? 3;
  if (!(concDmg > tgtSB) || ctx.newConds.includes('P')) return none();
  mergeCondition(ctx.newConds, 'P', ctx.stacking);
  const text = '💥 Concussive: ' + concDmg + ' damage exceeds SB ' + tgtSB + ' - knocked PRONE!';
  return report({ name: 'Concussive', icon: '💥', result: text, conditionApplied: 'P' });
}

/** The Stun test at −10 per X; stunned for its degrees of failure. v8.199.3: one d100 through the test engine. */
function concussiveStun(ctx: WeaponEffectContext): WeaponEffectResult {
  const concX = getWSP(ctx.specialRules, 'Concussive');
  const concPen = concX * 10;
  const t = rollPlainTargetTest(ctx.target, 'toughness', 0, -concPen);
  const head = '💥 Concussive(' + concX + '): ';
  const vs = t.roll + ' vs ' + t.target + ', penalty -' + concPen;
  if (t.success) return report(tested(CONCUSSIVE, t, true, head + 'Pass T-test (' + vs + ') - resists.'));
  const dof = Math.max(1, t.dof);
  mergeCondition(ctx.newConds, 'S' + dof, ctx.stacking);
  const text = head + 'FAIL T-test (' + vs + ', ' + dof + ' DoF) - STUNNED for ' + dof + ' round(s)!';
  return report(tested(CONCUSSIVE, t, false, text, { conditionApplied: 'S' + dof }));
}

registerWeaponEffect({
  name: 'Concussive',
  postHit(ctx) {
    if (inert(ctx)) return none();
    const prone = concussiveProne(ctx);
    return combine(prone, stunImmunity(ctx, CONCUSSIVE) ?? concussiveStun(ctx));
  },
});

const SNARE = { name: 'Snare', icon: '🕸' };

registerWeaponEffect({
  name: 'Snare',
  postHit(ctx) {
    if (inert(ctx)) return none();
    const x = getWSP(ctx.specialRules, 'Snare');
    // v8.199.3: through the test engine - same one d100, dodge base.
    const t = rollPlainTargetTest(ctx.target, 'dodge', 0, -x * 10);
    const head = '🕸 Snare(' + x + '): ';
    const vs = ' (' + t.roll + ' vs ' + t.target + ')';
    if (t.success) return report(tested(SNARE, t, true, head + 'Pass Ag-test' + vs + ' - resists.'));
    // 'Sn' (Snared), not 'Immobilised' (v8.198.2): identical combat
    // effect, but Snared carries the official per-turn escape test -
    // a GM-set Immobilised deliberately does not.
    if (!ctx.newConds.includes('Sn')) ctx.newConds.push('Sn');
    return report(tested(SNARE, t, false, head + 'FAIL Ag-test' + vs + ' - IMMOBILISED!', { conditionApplied: 'Sn' }));
  },
});

registerWeaponEffect({
  name: 'Corrosive',
  postHit(ctx) {
    const lastHit = inert(ctx) ? undefined : lastDamagingHit(ctx);
    if (!lastHit) return none();
    const apLoss = rollDie(10);
    const { oldAP, now, label } = stripArmour(ctx, lastHit.location, apLoss);
    // Official excess clause (v8.197.7): acid beyond the remaining armour
    // eats flesh - Toughness soaks (incl. unnatural), armour by definition
    // cannot.
    const soak = (ctx.target.toughnessBonus || 3) * (ctx.target.unnaturalToughness || 1);
    const bite = Math.max(0, apLoss - oldAP - soak);
    const corroded = '🧪 Corrosive: ' + label + ' armour corroded by ' + apLoss;
    const text =
      corroded +
      ' (was ' +
      oldAP +
      ', now ' +
      now +
      ').' +
      (bite > 0 ? ' Acid bites through - ' + bite + ' damage past TB ' + soak + '!' : '');
    const detail = {
      name: 'Corrosive',
      icon: '🧪',
      roll: apLoss,
      result: text,
      ...(bite > 0 ? { extraDamage: bite } : {}),
    };
    return report(detail, bite);
  },
});

/**
 * One drain's line. BOTH CURRENCIES ON THE LINE: the drain is typed in the
 * units its stat is shown in - BONUS points for Toughness/Strength/
 * Willpower - while tests are rolled in characteristic points, so the note
 * states the conversion rather than leaving a GM to wonder why a 2 became
 * a 20 (playtest, 2026-08-31).
 */
function drainLine(rule: string, char: DrainChar, amount: number, rounds: number): string {
  const testWorth = amount * (DRAIN_UNITS[char]?.testPerPoint ?? 1);
  const conversion = DRAIN_UNITS[char]?.unit ? ' (tests −' + testWorth + ')' : '';
  const span = rounds > 0 ? ' for ' + rounds + ' rounds' : ' for the scene';
  return '🩸 ' + rule + ': ' + drainUnitLabel(char) + ' −' + amount + span + conversion + '.';
}

// The stat-drain family (v8.290.0): six rules, one loop - a damaging
// hit drains a characteristic via the Dn: condition codes. Amounts sum
// and the duration refreshes (statDrains.ts owns the semantics);
// Nullifying is scene-long and only bites psykers.
for (const { rule, char } of DRAIN_TABLE) {
  registerWeaponEffect({
    name: rule,
    postHit(ctx) {
      const amount = getWSP(ctx.specialRules, rule);
      if (inert(ctx) || amount <= 0) return none();
      if (char === 'psy' && ((ctx.target as { psyRating?: number }).psyRating || 0) <= 0) return none();
      const rounds = char === 'psy' ? 0 : DRAIN_DURATION;
      mergeDrain(ctx.newConds, char, amount, rounds);
      return report({ name: rule, icon: '🩸', result: drainLine(rule, char, amount, rounds) });
    },
  });
}

// Disarming(X) (v8.287.0): on X+ degrees of success in melee, the
// target tests Strength or its guard is beaten aside - the Dsm
// condition suspends parry for a round (no dropped-item bookkeeping;
// the weapon is wrenched, not lost).
registerWeaponEffect({
  name: 'Disarming',
  postHit(ctx) {
    const threshold = getWSP(ctx.specialRules, 'Disarming');
    const isMelee = (ctx.slot as { weaponType?: string })?.weaponType === 'melee';
    if (!isMelee || threshold <= 0 || ctx.dos < threshold || ctx.targetKilled) return none();
    const test = rollPlainTargetTest(ctx.target as never, 'strength', 0, 0);
    const vs = '🗡 Disarming: Strength ' + test.roll + ' vs ' + test.target;
    if (test.success)
      return report({ name: 'Disarming', icon: '🗡', roll: test.roll, result: vs + ' - the grip holds.' });
    mergeCondition(ctx.newConds, 'Dsm1', ctx.stacking);
    const text = vs + ' FAILED - guard beaten aside, no Parry for a round!';
    return report({ name: 'Disarming', icon: '🗡', roll: test.roll, result: text, conditionApplied: 'Dsm1' });
  },
});

// Suppressing(X) (v8.286.0): a damaging hit halts the target's
// regeneration for X rounds (min 1) - the Rg condition, consumed by the
// countdown's regen gate. No per-wound bookkeeping: the whole point.
registerWeaponEffect({
  name: 'Suppressing',
  postHit(ctx) {
    if (inert(ctx) || !((ctx.target.regeneration || 0) > 0)) return none();
    const rounds = Math.max(1, getWSP(ctx.specialRules, 'Suppressing'));
    mergeCondition(ctx.newConds, 'Rg' + rounds, ctx.stacking);
    const text = '🕯 Suppressing: regeneration halted for ' + rounds + ' round' + (rounds > 1 ? 's' : '') + '.';
    return report({ name: 'Suppressing', icon: '🕯', result: text, conditionApplied: 'Rg' + rounds });
  },
});

// Sunder(X) (v8.284.0): the flat, mechanical sibling of Corrosive - a
// power-fist crushes X armour off the struck location, no dice, no
// excess clause (the crushing already did its damage as the hit).
registerWeaponEffect({
  name: 'Sunder',
  postHit(ctx) {
    if (inert(ctx)) return none();
    const sunderVal = getWSP(ctx.specialRules, 'Sunder');
    const lastHit = lastDamagingHit(ctx);
    if (!(sunderVal > 0 && lastHit)) return none();
    const { oldAP, now, label } = stripArmour(ctx, lastHit.location, sunderVal);
    const text = '🔨 Sunder: ' + label + ' armour crushed by ' + sunderVal + ' (was ' + oldAP + ', now ' + now + ').';
    return report({ name: 'Sunder', icon: '🔨', result: text });
  },
});

/** One critical's crippling, unless that location is crippled already. */
function crippleFrom(ctx: WeaponEffectContext, h: HitResult): WeaponEffectResult {
  const cripCode = 'Cr:' + (h.location.name || 'Unknown');
  if (!h.crit || h.ffBlocked || h.critSev <= 0 || ctx.newConds.some((c) => c === cripCode)) return none();
  ctx.newConds.push(cripCode);
  const text = '🦴 Crippling: ' + h.location.name + ' is CRIPPLED by the critical wound!';
  return report({ name: 'Crippling', icon: '🦴', result: text, conditionApplied: cripCode });
}

registerWeaponEffect({
  name: 'Crippling',
  postHit(ctx) {
    if (ctx.targetKilled) return none();
    // In hit order: each crit's crippling sees the ones before it.
    return combine(...ctx.hits.map((h) => crippleFrom(ctx, h)));
  },
});

registerWeaponEffect({
  name: 'Force',
  postHit(ctx) {
    const pr = ctx.slot.psyRating || 0;
    const text =
      pr > 0
        ? '🔮 Force weapon: Psy Rating ' + pr + ' added to damage. GM may add to Pen.'
        : '🔮 Force weapon active. Set Psy Rating in weapon editor.';
    return report({ name: 'Force', icon: '🔮', result: text });
  },
});

const HAYWIRE_ROWS: Record<number, string> = {
  1: 'unshielded devices flicker and stutter',
  2: 'technology fails outright while the field holds',
  3: 'a dead zone - nothing electrical answers',
  4: 'TOTAL SHUTDOWN - every system stops, even armour seizes',
};

registerWeaponEffect({
  name: 'Haywire',
  postHit(ctx) {
    if (inert(ctx)) return none();
    // The five official rows (v8.198.8). One roll per ATTACK - made at
    // the shape in useAttackExecution and shared by every victim; the
    // fallback roll here serves direct engine callers and old fixtures.
    const roll =
      ctx.haywireRoll && ctx.haywireRoll > 0 ? ctx.haywireRoll : rollDie(10) + getWSP(ctx.specialRules, 'Haywire');
    const severity = roll <= 2 ? 0 : Math.min(4, Math.floor((roll - 3) / 2) + 1);
    const head = '⚡ Haywire (' + roll + '): ';
    if (severity === 0) {
      return report({
        name: 'Haywire',
        icon: '⚡',
        roll,
        result: head + 'Insignificant - the field sputters and dies.',
      });
    }
    const code = 'Hw' + severity;
    // mergeCondition keeps the HIGHER field and never sums (conditions.ts)
    mergeCondition(ctx.newConds, code, ctx.stacking);
    const text =
      head + haywireSeverityName(severity) + ' - ' + HAYWIRE_ROWS[severity] + '. Weakens one step each round.';
    return report({ name: 'Haywire', icon: '⚡', roll, result: text, conditionApplied: code });
  },
});

registerWeaponEffect({
  name: 'Spray',
  postHit() {
    // The cone is the WEAPON's range and the rule's own angle now, not a
    // hard-coded thirty metres - see `sprayAngleOf`. The Agility test is
    // rolled per victim at the moment of firing, so this is a summary of what
    // happened rather than an instruction to the GM.
    const text = '🔥 Spray: cone auto-hit, Agility test to avoid. Ignores BS, range and cover.';
    return report({ name: 'Spray', icon: '🔥', result: text });
  },
});

registerWeaponEffect({
  name: 'Pinning',
  postHit(ctx) {
    // Not gated on dealtDamage: pinning is fear of fire, not the wound -
    // a hit that bounced off armour still sends a man to the ground.
    if (ctx.targetKilled) return none();
    // The test itself lives in engine/pinning since v8.306.1, because
    // Suppressing Fire needs the same one at Hard (-20). Purgatus pins
    // what cannot otherwise be pinned; an immune subject rolls nothing,
    // which the seeded dice order depends on.
    // The shooter's `pin` exposure (v8.368.5): Withering fire pins at −10.
    const pinMod = reactionExposure(
      { exposures: ctx.attackerExposures ?? [], teamTag: ctx.attackerTeamTag ?? null, sideTag: ctx.attackerSideTag },
      { teamTag: ctx.target.teamTag ?? null, sideTag: ctx.target.sideTag, isMelee: false },
    ).pinMod;
    const out = rollPinningTest(ctx.target, { mod: pinMod, ignoreImmunity: hasWS(ctx.specialRules, 'Purgatus') });
    // The exposed modifier names itself (v8.368.9): the ledger shows why the target moved.
    const exposedNote = pinMod ? ` (exposed ${pinMod > 0 ? '+' : ''}${pinMod})` : '';
    const text = '📌 Pinning: ' + out.summary + exposedNote;
    if (out.pinned && !ctx.newConds.includes('Pn')) ctx.newConds.push('Pn');
    return report({
      name: 'Pinning',
      icon: '📌',
      ...(out.test ? { roll: out.test.roll, target: out.test.target, success: out.test.success } : {}),
      result: text,
      ...(out.pinned ? { conditionApplied: 'Pn' } : {}),
    });
  },
});
